import { useRef } from 'react';

interface Product {
  id: number;
  name: string;
  image: string;
  price: string;
  priceCny: string;
  priceUsd: string;
  url: string;
}

interface ProductCarouselProps {
  categoryName: string;
  categoryUrl: string;
  products: Product[];
}

export default function ProductCarousel({ categoryName, categoryUrl, products }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <div className="w-full">
      {/* Category Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <h3 className="text-lg md:text-xl font-bold text-foreground-950">
          {categoryName}
        </h3>
        <div className="flex items-center gap-2">
          <a
            href={categoryUrl}
            target="_blank"
            rel="noopener nofollow"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors hidden sm:flex items-center gap-1"
          >
            View All
            <i className="ri-arrow-right-line" />
          </a>
          <button
            onClick={() => scroll('left')}
            className="w-8 h-8 rounded-full bg-background-100 border border-background-200 flex items-center justify-center text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-8 h-8 rounded-full bg-background-100 border border-background-200 flex items-center justify-center text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>

      {/* Products Scroll Container */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => (
          <a
            key={product.id}
            href={product.url}
            target="_blank"
            rel="noopener nofollow"
            className="group flex-shrink-0 w-44 sm:w-52 snap-start bg-white rounded-xl border border-background-200 overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1"
          >
            <div className="relative w-full aspect-square bg-background-50 overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="p-3">
              <h4 className="text-xs font-medium text-foreground-900 leading-snug line-clamp-2 mb-2 min-h-[2.5rem]">
                {product.name}
              </h4>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-primary-600">{product.priceUsd}</span>
                <span className="text-xs text-foreground-500">{product.priceCny}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}